import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import AvatarCropDialog from '@/components/AvatarCropDialog';
import ConnectBankButton from '@/components/ConnectBankButton';
import FamilyProfilesSection from '@/components/family/FamilyProfilesSection';
import HouseholdInviteSection from '@/components/family/HouseholdInviteSection';
import UkDateField from '@/components/UkDateField';
import { AdultingMark, AppleMark, formatCalendarSyncStatus, formatHomeRoutineReminderStyle, formatPushStatus, GoogleMark, ProviderButtonLabel } from '@/components/settings/SettingsUi';
import type { Child, DependantType, UserProfile, WorkType } from '@/types';
import { supabase } from '@/integrations/supabase/client';
import { ArrowLeft, ImagePlus, Plus, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  loadIntegrationSettings,
  saveIntegrationSettings,
  type IntegrationSettingsRecord,
} from '@/lib/integration-settings';
import { completeBankConnection } from '@/lib/open-banking';
import {
  updateSetupCalendarMode,
  updateSetupDocumentsMode,
} from '@/lib/integration-setup-actions';
import {
  ADULTING_DESTINATION_OPTION_LABELS,
  ADULTING_NAVIGATION_PREFERENCES_CHANGED_EVENT,
  ADULTING_PINNED_SECTIONS_STORAGE_KEY,
  ADULTING_SECTION_OPTION_LABELS,
  getAdultingDestinationPlainLabel,
  getAvailableAdultingSections,
  getAvailableAdultingNavigationDestinations,
  getAdultingOverflowDestinations,
  isAdultingNavigationDestinationPath,
  isAdultingSectionPath,
  keepAdultingDestinationUnderMore,
  moveAdultingDestinationEarlier,
  moveAdultingDestinationLater,
  pinAdultingDestination,
  resolveAdultingPinnedDestinations,
  type AdultingNavigationDestinationPath,
  type AdultingSectionPath,
} from '../../ui-foundation/core';
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppCardHeader,
  AppEmptyState,
  AppFieldGroup,
  AppInlineAlert,
  AppInput,
  AppNativeSelect,
  AppPage,
  AppPageIntro,
  AppSectionHeader,
  AppText,
} from '../../ui-foundation';
import {
  collectSelectedSchoolNames,
  type FamilySchoolLookupCandidate,
  isChildSchoolSelectionRelevant,
  resolveSchoolLookupPostcode,
} from '../../ui-foundation/features/family';
import {
  HOME_ROUTINE_REMINDER_STYLE_OPTIONS,
  getHomeRoutineReminderStyleLabel,
  type ReminderStrength,
} from '../../ui-foundation/features/notifications';
import {
  FINANCE_BANK_ACCOUNT_ROLE_OPTIONS,
  buildManualFinanceSetupAnnualIncome,
  buildManualFinanceSetupBillRows,
  buildManualFinanceSetupDraft,
  buildManualFinanceSetupHomeProfilePatch,
  normaliseFinanceBankAccountRole,
  getManualFinanceHousingCostLabel,
  getManualFinanceHousingProviderLabel,
  hasManualFinanceSetupInput,
  type FinanceBankAccountRole,
  type ManualFinanceSetupDraft,
  type ManualFinanceSetupSeedBill,
} from '../../ui-foundation/features/money';
import { hasTrackedPets } from '../../ui-foundation/features/pets';
import {
  AppAccordion as Accordion,
  AppBadge,
  AppAccordionContent as AccordionContent,
  AppAccordionItem as AccordionItem,
  AppAccordionTrigger as AccordionTrigger,
  AppAvatar as Avatar,
  AppAvatarFallback as AvatarFallback,
  AppAvatarImage as AvatarImage,
  AppSelect as Select,
  AppSelectContent as SelectContent,
  AppSelectItem as SelectItem,
  AppSelectTrigger as SelectTrigger,
  AppSelectValue as SelectValue,
} from '../../ui-foundation/adapters/web';
import {
  SETUP_FINANCE_HANDOFF_NOTE,
  SETUP_FINANCE_SECTION_SUBTITLE,
  SETUP_FINANCE_SECTION_TITLE,
} from '../../ui-foundation/features/onboarding';
import {
  buildAccountSetupStatus,
  buildHomeSetupStatus,
  buildMoneySetupStatus,
  buildStorageCalendarSetupStatus,
  type SetupStatusSummary,
  type SetupStatusTone,
} from '../../ui-foundation/features/onboarding';

const WORK_OPTIONS: { value: WorkType; emoji: string; label: string; note: string }[] = [
  { value: 'employed', emoji: '🏢', label: 'Employed', note: 'Payslips, rights, sick pay, contracts' },
  { value: 'self-employed', emoji: '💼', label: 'Self-employed', note: 'HMRC, invoicing, expenses' },
  { value: 'mix', emoji: '🔀', label: 'Mix of both', note: 'Use both employee and freelance tools' },
  { value: 'not-working', emoji: '🏠', label: 'Not working', note: 'Hide work-specific admin for now' },
];

const DEPENDANT_OPTIONS: { value: DependantType | 'none'; emoji: string; label: string }[] = [
  { value: 'children', emoji: '👶', label: 'Children' },
  { value: 'elderly', emoji: '👴', label: 'Older family member' },
  { value: 'disabled', emoji: '🩺', label: 'Disabled or unwell family member' },
  { value: 'none', emoji: '🙋', label: 'No dependants right now' },
];

const SCREENING_PATH_OPTIONS = [
  { value: 'female', label: 'Usually invited on the female path' },
  { value: 'male', label: 'Usually invited on the male path' },
  { value: 'other', label: 'Other / not sure / prefer not to say' },
] as const;

function profileDisplayName(firstName?: string, email?: string | null) {
  const trimmedFirstName = firstName?.trim();
  if (trimmedFirstName) return trimmedFirstName;

  return email?.split('@')[0]?.trim() || 'Your profile';
}

type HomeSetupSummary = {
  address: string | null;
  tenure: string | null;
  councilTaxBand: string | null;
  hasLawn: boolean | null;
  hasHouseplants: boolean | null;
  vehicleRegistrationNumber: string | null;
  vehicleMotDueDate: string | null;
  vehicleTaxDueDate: string | null;
  monthlyRent: number | null;
  landlordName: string | null;
  mortgageProvider: string | null;
  utilityGasProvider: string | null;
  utilityElectricProvider: string | null;
  utilityBroadbandProvider: string | null;
  utilityBroadbandContractEnd: string | null;
  utilityPhoneProvider: string | null;
};

type BankSetupSummary = {
  connectedAccountCount: number;
  latestSyncedAt: string | null;
};

type BankSetupAccountRoleRow = {
  id: string;
  institutionName: string | null;
  name: string | null;
  accountType: string | null;
  accountRole: FinanceBankAccountRole;
  savedAccountRole: FinanceBankAccountRole;
  lastSynced: string | null;
};

function formatSetupDate(value: string | null | undefined) {
  if (!value) return 'Not set';
  return new Date(value).toLocaleDateString('en-GB');
}

function formatTenureLabel(value: string | null | undefined) {
  if (!value) return 'Not set';
  return value
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatBankAccountTypeLabel(value: string | null | undefined) {
  if (!value) return null;
  return value
    .replace(/_/g, ' ')
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function formatDocumentsMode(settings: IntegrationSettingsRecord | null) {
  if (!settings) return 'Loading…';
  if (settings.documents_mode === 'google_drive') {
    return `Google Drive${settings.documents_root_folder_name ? ` · ${settings.documents_root_folder_name}` : ''}`;
  }
  if (settings.documents_mode === 'icloud') {
    return 'Apple Files';
  }
  return 'Adulting storage';
}

function formatCalendarMode(settings: IntegrationSettingsRecord | null) {
  if (!settings) return 'Loading…';
  if (settings.calendar_mode === 'google_calendar') {
    return `Google Calendar${settings.calendar_external_name ? ` · ${settings.calendar_external_name}` : ''}`;
  }
  if (settings.calendar_mode === 'icloud') {
    return 'Apple Calendar';
  }
  return 'Adulting calendar';
}

function getSetupStatusBadgeClassName(tone: SetupStatusTone) {
  if (tone === 'success') {
    return 'shrink-0 border-transparent bg-success/10 text-success hover:bg-success/10';
  }

  if (tone === 'progress') {
    return 'shrink-0 border-transparent bg-secondary/10 text-secondary-foreground hover:bg-secondary/10';
  }

  return 'shrink-0 border-border bg-muted text-muted-foreground hover:bg-muted';
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, signOut, updateProfile, updateProfileAvatar } = useApp();
  const [profile, setProfile] = useState<UserProfile>(data.profile);
  const [saving, setSaving] = useState(false);
  const [childSchoolCandidates, setChildSchoolCandidates] = useState<FamilySchoolLookupCandidate[]>([]);
  const [childSchoolLookupBusy, setChildSchoolLookupBusy] = useState(false);
  const [childSchoolLookupError, setChildSchoolLookupError] = useState<string | null>(null);
  const [schoolCalendarImportBusy, setSchoolCalendarImportBusy] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [avatarDraftUrl, setAvatarDraftUrl] = useState<string | null>(null);
  const [homeSetup, setHomeSetup] = useState<HomeSetupSummary | null>(null);
  const [homeRoutineDraft, setHomeRoutineDraft] = useState(() => ({
    hasLawn: null as boolean | null,
    hasHouseplants: null as boolean | null,
  }));
  const [homeRoutineSaving, setHomeRoutineSaving] = useState(false);
  const [homeRoutineSaved, setHomeRoutineSaved] = useState(false);
  const [bankSetup, setBankSetup] = useState<BankSetupSummary | null>(null);
  const [bankSetupAccounts, setBankSetupAccounts] = useState<BankSetupAccountRoleRow[]>([]);
  const [integrationSetup, setIntegrationSetup] = useState<IntegrationSettingsRecord | null>(null);
  const [financeSetupBills, setFinanceSetupBills] = useState<ManualFinanceSetupSeedBill[]>([]);
  const [manualFinanceDraft, setManualFinanceDraft] = useState<ManualFinanceSetupDraft>(() => buildManualFinanceSetupDraft({}));
  const [manualFinanceSaving, setManualFinanceSaving] = useState(false);
  const [manualFinanceSaved, setManualFinanceSaved] = useState(false);
  const [setupLoading, setSetupLoading] = useState(true);
  const [homeReminderStyleSaving, setHomeReminderStyleSaving] = useState(false);
  const [defaultSection, setDefaultSection] = useState<AdultingSectionPath | ''>('');
  const [preferredPinnedDestinations, setPreferredPinnedDestinations] = useState<AdultingNavigationDestinationPath[] | null>(null);
  const [documentsModeSaving, setDocumentsModeSaving] = useState(false);
  const [calendarModeSaving, setCalendarModeSaving] = useState(false);
  const [resetSending, setResetSending] = useState(false);
  const profileDraftRef = useRef<UserProfile>(data.profile);
  const childSchoolLookupAutoKeyRef = useRef('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const currentProfileLabel = profileDisplayName(profile.firstName, data.user?.email);
  const hasChildren = data.profile.children.some((child) => child.name.trim().length > 0);
  const hasPetDestination = hasTrackedPets(data.pets);
  const navigationPreferenceContext = useMemo(
    () => ({
      workType: data.profile.workType,
      dependantTypes: data.profile.dependantTypes,
      hasChildren,
      partnerName: data.profile.partnerName,
      hasHouseholdShare: Boolean(data.householdShare),
      communityAccessStatus: data.profile.communityAccessStatus,
      hasTrackedPets: hasPetDestination,
    }),
    [
      data.householdShare,
      data.profile.communityAccessStatus,
      data.profile.dependantTypes,
      data.profile.partnerName,
      data.profile.workType,
      hasChildren,
      hasPetDestination,
    ],
  );
  const homeSetupStatus = buildHomeSetupStatus(setupLoading ? { loading: true } : homeSetup);
  const moneySetupStatus = buildMoneySetupStatus(setupLoading ? { loading: true } : bankSetup);
  const storageCalendarSetupStatus = buildStorageCalendarSetupStatus(
    setupLoading
      ? { loading: true }
      : {
          documentsMode: integrationSetup?.documents_mode,
          calendarMode: integrationSetup?.calendar_mode,
        },
  );
  const accountSetupStatus = buildAccountSetupStatus(
    setupLoading
      ? { loading: true }
      : {
          pushNotificationsEnabled: integrationSetup?.push_notifications_enabled,
          defaultSection,
        },
  );
  const sectionOptions = useMemo(() => {
    const availableSections = getAvailableAdultingSections(navigationPreferenceContext);

    return [
      { value: '' as const, label: 'Remember last section' },
      ...availableSections.map((value) => ({
        value,
        label: ADULTING_SECTION_OPTION_LABELS[value],
      })),
    ];
  }, [navigationPreferenceContext]);
  const navigationDestinationOptions = useMemo(
    () =>
      getAvailableAdultingNavigationDestinations(navigationPreferenceContext),
    [navigationPreferenceContext],
  );
  const resolvedPinnedDestinations = useMemo(
    () =>
      resolveAdultingPinnedDestinations({
        preferred: preferredPinnedDestinations,
        context: navigationPreferenceContext,
      }),
    [navigationPreferenceContext, preferredPinnedDestinations],
  );
  const overflowDestinations = useMemo(
    () =>
      getAdultingOverflowDestinations({
        preferred: preferredPinnedDestinations,
        context: navigationPreferenceContext,
      }),
    [navigationPreferenceContext, preferredPinnedDestinations],
  );

  useEffect(() => {
    setProfile(data.profile);
    profileDraftRef.current = data.profile;
  }, [data.profile]);

  useEffect(() => {
    const storedPinnedSections = localStorage.getItem(ADULTING_PINNED_SECTIONS_STORAGE_KEY);
    if (!storedPinnedSections) {
      setPreferredPinnedDestinations(null);
      return;
    }

    try {
      const parsedPinnedSections = JSON.parse(storedPinnedSections);
      if (!Array.isArray(parsedPinnedSections)) {
        setPreferredPinnedDestinations(null);
        return;
      }

      setPreferredPinnedDestinations(parsedPinnedSections.filter(isAdultingNavigationDestinationPath));
    } catch {
      setPreferredPinnedDestinations(null);
    }
  }, [navigationDestinationOptions]);

  useEffect(() => () => {
    if (avatarDraftUrl) {
      URL.revokeObjectURL(avatarDraftUrl);
    }
  }, [avatarDraftUrl]);

  useEffect(() => {
    setHomeRoutineDraft({
      hasLawn: homeSetup?.hasLawn ?? null,
      hasHouseplants: homeSetup?.hasHouseplants ?? null,
    });
  }, [homeSetup?.hasHouseplants, homeSetup?.hasLawn]);

  useEffect(() => {
    let active = true;

    const loadSetupSummary = async () => {
      if (!data.user?.id) {
        if (!active) return;
        setHomeSetup(null);
        setBankSetup(null);
        setBankSetupAccounts([]);
        setIntegrationSetup(null);
        setFinanceSetupBills([]);
        setDefaultSection('');
        setSetupLoading(false);
        return;
      }

      setSetupLoading(true);

      try {
        const [homeResult, bankResult, integrationResult, financeBillsResult] = await Promise.all([
          supabase
            .from('home_profiles' as any)
            .select('address, tenure, council_tax_band, has_lawn, has_houseplants, vehicle_registration_number, vehicle_mot_due_date, vehicle_tax_due_date, monthly_rent, landlord_name, mortgage_provider, utility_gas_provider, utility_electric_provider, utility_broadband_provider, utility_broadband_contract_end, utility_phone_provider')
            .eq('user_id', data.user.id)
            .maybeSingle(),
          supabase
            .from('bank_accounts' as any)
            .select('*')
            .eq('user_id', data.user.id),
          loadIntegrationSettings(data.user.id),
          supabase
            .from('home_bill_settings' as any)
            .select('bill_key, amount, provider_name')
            .eq('user_id', data.user.id),
        ]);

        if (!active) {
          return;
        }

        if (homeResult.error) {
          throw homeResult.error;
        }

        if (bankResult.error) {
          throw bankResult.error;
        }
        if (financeBillsResult.error) {
          throw financeBillsResult.error;
        }

        setHomeSetup(
          homeResult.data
            ? {
                address: homeResult.data.address || null,
                tenure: homeResult.data.tenure || null,
                councilTaxBand: homeResult.data.council_tax_band || null,
                hasLawn: homeResult.data.has_lawn ?? null,
                hasHouseplants: homeResult.data.has_houseplants ?? null,
                vehicleRegistrationNumber: homeResult.data.vehicle_registration_number || null,
                vehicleMotDueDate: homeResult.data.vehicle_mot_due_date || null,
                vehicleTaxDueDate: homeResult.data.vehicle_tax_due_date || null,
                monthlyRent: homeResult.data.monthly_rent ?? null,
                landlordName: homeResult.data.landlord_name || null,
                mortgageProvider: homeResult.data.mortgage_provider || null,
                utilityGasProvider: homeResult.data.utility_gas_provider || null,
                utilityElectricProvider: homeResult.data.utility_electric_provider || null,
                utilityBroadbandProvider: homeResult.data.utility_broadband_provider || null,
                utilityBroadbandContractEnd: homeResult.data.utility_broadband_contract_end || null,
                utilityPhoneProvider: homeResult.data.utility_phone_provider || null,
              }
            : null,
        );

        const latestSyncedAt = (bankResult.data || []).reduce<string | null>((latest, row) => {
          const nextValue = row.last_synced as string | null;
          if (!nextValue) return latest;
          if (!latest) return nextValue;
          return new Date(nextValue).getTime() > new Date(latest).getTime() ? nextValue : latest;
        }, null);

        setBankSetup({
          connectedAccountCount: bankResult.data?.length || 0,
          latestSyncedAt,
        });
        setBankSetupAccounts(
          ((bankResult.data || []) as Array<{
            id: string;
            institution_name?: string | null;
            name?: string | null;
            account_type?: string | null;
            account_role?: string | null;
            last_synced?: string | null;
          }>).map((row) => {
            const normalisedRole = normaliseFinanceBankAccountRole(row.account_role);

            return {
              id: row.id,
              institutionName: row.institution_name || null,
              name: row.name || null,
              accountType: row.account_type || null,
              accountRole: normalisedRole,
              savedAccountRole: normalisedRole,
              lastSynced: row.last_synced || null,
            };
          }),
        );
        setIntegrationSetup(integrationResult);
        setFinanceSetupBills(
          ((financeBillsResult.data || []) as Array<{ bill_key: string; amount: number | null; provider_name: string | null }>)
            .map((row) => ({
              billKey: row.bill_key as ManualFinanceSetupSeedBill['billKey'],
              amount: row.amount,
              providerName: row.provider_name,
            })),
        );

        const savedDefaultSection = localStorage.getItem('adulting_default_section') || '';
        setDefaultSection(isAdultingSectionPath(savedDefaultSection) ? savedDefaultSection : '');
      } catch (error) {
        console.error(error);
        if (!active) {
          return;
        }
        setHomeSetup(null);
        setBankSetup(null);
        setBankSetupAccounts([]);
        setIntegrationSetup(null);
        setFinanceSetupBills([]);
        setDefaultSection('');
      } finally {
        if (active) {
          setSetupLoading(false);
        }
      }
    };

    void loadSetupSummary();

    const handleIntegrationSettingsChanged = () => {
      void loadSetupSummary();
    };

    window.addEventListener('adulting:integration-settings-changed', handleIntegrationSettingsChanged);

    return () => {
      active = false;
      window.removeEventListener('adulting:integration-settings-changed', handleIntegrationSettingsChanged);
    };
  }, [data.user?.id]);

  useEffect(() => {
    const authError = searchParams.get('error');
    const authErrorDescription = searchParams.get('error_description');

    if (authError) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('error');
      nextParams.delete('error_description');
      nextParams.delete('scope');
      setSearchParams(nextParams, { replace: true });
      toast.error(authErrorDescription || 'Bank connection was cancelled or could not be completed.');
      return;
    }

    const authCode = searchParams.get('code');
    const authState = searchParams.get('state');

    if (!authCode && !authState) {
      return;
    }

    if (!data.user?.id) {
      return;
    }

    const callbackUrl = typeof window !== 'undefined' ? window.location.href : undefined;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('code');
    nextParams.delete('state');
    nextParams.delete('scope');
    setSearchParams(nextParams, { replace: true });

    if (!authCode || !authState) {
      toast.error('Bank connection did not return the details Adulting needed. Please try again.');
      return;
    }

    void completeBankConnection(
      authCode,
      authState,
      `${window.location.origin}/profile`,
      data.user.id,
      callbackUrl,
    )
      .then(({ accounts, transactionCount }) => {
        const accountSummary = accounts.length
          ? `${accounts.length} bank account${accounts.length === 1 ? '' : 's'} connected`
          : 'Bank connection completed';
        const detail = transactionCount
          ? `${accountSummary}. ${transactionCount} transaction${transactionCount === 1 ? '' : 's'} imported.`
          : `${accountSummary}.`;
        toast.success(detail);
        window.dispatchEvent(new CustomEvent('adulting:integration-settings-changed'));
        window.dispatchEvent(new CustomEvent('adulting:data-changed'));
      })
      .catch((error) => {
        console.error(error);
        toast.error(error instanceof Error ? error.message : 'Could not finish the bank connection.');
      });
  }, [data.user?.id, searchParams, setSearchParams]);

  useEffect(() => {
    setManualFinanceDraft(buildManualFinanceSetupDraft({
      annualIncome: profile.annualIncome || null,
      tenure: homeSetup?.tenure || null,
      monthlyRent: homeSetup?.monthlyRent || null,
      landlordName: homeSetup?.landlordName || null,
      mortgageProvider: homeSetup?.mortgageProvider || null,
      utilityGasProvider: homeSetup?.utilityGasProvider || null,
      utilityElectricProvider: homeSetup?.utilityElectricProvider || null,
      utilityBroadbandProvider: homeSetup?.utilityBroadbandProvider || null,
      utilityBroadbandContractEnd: homeSetup?.utilityBroadbandContractEnd || null,
      utilityPhoneProvider: homeSetup?.utilityPhoneProvider || null,
      bills: financeSetupBills,
    }));
  }, [
    financeSetupBills,
    homeSetup?.landlordName,
    homeSetup?.monthlyRent,
    homeSetup?.mortgageProvider,
    homeSetup?.tenure,
    homeSetup?.utilityBroadbandContractEnd,
    homeSetup?.utilityBroadbandProvider,
    homeSetup?.utilityElectricProvider,
    homeSetup?.utilityGasProvider,
    homeSetup?.utilityPhoneProvider,
    profile.annualIncome,
  ]);

  useEffect(() => {
    if (!defaultSection) {
      return;
    }

    if (!sectionOptions.some((option) => option.value === defaultSection)) {
      setDefaultSection('');
      localStorage.removeItem('adulting_default_section');
    }
  }, [defaultSection, sectionOptions]);

  const setDraftProfile = (nextProfile: UserProfile | ((current: UserProfile) => UserProfile)) => {
    setProfile((current) => {
      const resolvedProfile = typeof nextProfile === 'function'
        ? nextProfile(current)
        : nextProfile;
      profileDraftRef.current = resolvedProfile;
      return resolvedProfile;
    });
  };

  const activeChildren = useMemo(
    () => profile.children.filter((child) => child.name.trim().length > 0),
    [profile.children]
  );
  const schoolEligibleChildren = useMemo(
    () => profile.children.filter((child) => isChildSchoolSelectionRelevant({
      dateOfBirth: child.dateOfBirth || null,
      age: child.age,
    })),
    [profile.children],
  );
  const selectedSchoolNames = useMemo(
    () => collectSelectedSchoolNames(schoolEligibleChildren),
    [schoolEligibleChildren],
  );
  const childSchoolLookupPostcode = useMemo(
    () => resolveSchoolLookupPostcode({
      postcode: profile.postcode,
      address: homeSetup?.address || null,
    }),
    [homeSetup?.address, profile.postcode],
  );
  const childSchoolLookupAutoKey = schoolEligibleChildren.length && childSchoolLookupPostcode
    ? childSchoolLookupPostcode
    : '';

  const toggleDependant = (value: DependantType | 'none') => {
    if (value === 'none') {
      setDraftProfile((current) => ({ ...current, dependantTypes: [] }));
      return;
    }

    setDraftProfile((current) => ({
      ...current,
      dependantTypes: current.dependantTypes.includes(value)
        ? current.dependantTypes.filter((item) => item !== value)
        : [...current.dependantTypes, value],
    }));
  };

  const setNullableFlag = (
    key: 'hasCervix' | 'breastScreeningRelevant' | 'prostateScreeningRelevant',
    value: boolean
  ) => {
    setDraftProfile((current) => ({
      ...current,
      [key]: current[key] === value ? null : value,
    }));
  };

  const addChild = () => {
    setDraftProfile((current) => ({
      ...current,
      dependantTypes: current.dependantTypes.includes('children')
        ? current.dependantTypes
        : [...current.dependantTypes, 'children'],
      children: [...current.children, { id: crypto.randomUUID(), name: '', age: 0, dateOfBirth: '', schoolName: '', schoolUrn: '' }],
    }));
  };

  const removeChild = (id: string) => {
    const nextChildren = profile.children.filter((child) => child.id !== id);
    const hasAnyNamedChildren = nextChildren.some((child) => child.name.trim().length > 0);

    setDraftProfile((current) => ({
      ...current,
      dependantTypes: hasAnyNamedChildren
        ? current.dependantTypes
        : current.dependantTypes.filter((item) => item !== 'children'),
      children: nextChildren,
    }));
  };

  const updateChild = (id: string, field: 'name' | 'dateOfBirth', value: string) => {
    setDraftProfile((current) => ({
      ...current,
      dependantTypes: current.dependantTypes.includes('children')
        ? current.dependantTypes
        : [...current.dependantTypes, 'children'],
      children: current.children.map((child) => {
        if (child.id !== id) {
          return child;
        }

        const nextChild = { ...child, [field]: value };
        if (field === 'dateOfBirth' && !isChildSchoolSelectionRelevant({ dateOfBirth: value })) {
          nextChild.schoolName = '';
          nextChild.schoolUrn = '';
        }
        return nextChild;
      }),
    }));
  };

  const assignChildSchool = (id: string, schoolUrn: string) => {
    const match = childSchoolCandidates.find((candidate) => candidate.urn === schoolUrn);
    setDraftProfile((current) => ({
      ...current,
      dependantTypes: current.dependantTypes.includes('children')
        ? current.dependantTypes
        : [...current.dependantTypes, 'children'],
      children: current.children.map((child) => (
        child.id === id
          ? {
              ...child,
              schoolName: match?.name || '',
              schoolUrn: match?.urn || '',
            }
          : child
      )),
    }));
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      const draftProfile = profileDraftRef.current;
      await updateProfile({
        ...draftProfile,
        children: draftProfile.children.filter((child) => child.name.trim().length > 0),
      });
      toast.success('Profile saved.');
    } catch (error) {
      console.error(error);
      toast.error('Could not save your profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveManualFinanceBasics = async () => {
    if (!data.user?.id) {
      return;
    }

    const changedBankAccountRoles = bankSetupAccounts.filter((row) => row.accountRole !== row.savedAccountRole);
    const hasManualFinanceChanges = hasManualFinanceSetupInput(manualFinanceDraft);

    if (!hasManualFinanceChanges && changedBankAccountRoles.length === 0) {
      toast.error('Add at least one finance basic or connected-account role first.');
      return;
    }

    setManualFinanceSaving(true);

    try {
      const annualIncome = hasManualFinanceChanges
        ? buildManualFinanceSetupAnnualIncome(manualFinanceDraft)
        : null;
      const homePatch = hasManualFinanceChanges
        ? buildManualFinanceSetupHomeProfilePatch(data.user.id, homeSetup?.tenure || null, manualFinanceDraft)
        : { user_id: data.user.id };
      const billRows = hasManualFinanceChanges
        ? buildManualFinanceSetupBillRows(data.user.id, homeSetup?.tenure || null, manualFinanceDraft)
        : [];
      const tasks: Array<Promise<{ error: Error | null }>> = [];

      if (annualIncome != null) {
        tasks.push(
          supabase.from('profiles').upsert(
            {
              user_id: data.user.id,
              annual_income: annualIncome,
            } as any,
            { onConflict: 'user_id' },
          ) as Promise<{ error: Error | null }>,
        );
      }

      if (Object.keys(homePatch).length > 1) {
        tasks.push(
          (supabase.from('home_profiles') as any).upsert(
            homePatch,
            { onConflict: 'user_id' },
          ) as Promise<{ error: Error | null }>,
        );
      }

      if (billRows.length) {
        tasks.push(
          (supabase.from('home_bill_settings' as any) as any).upsert(
            billRows,
            { onConflict: 'user_id,bill_key' },
          ) as Promise<{ error: Error | null }>,
        );
      }

      changedBankAccountRoles.forEach((row) => {
        tasks.push(
          (supabase.from('bank_accounts' as any) as any)
            .update({ account_role: row.accountRole })
            .eq('id', row.id)
            .eq('user_id', data.user.id) as Promise<{ error: Error | null }>,
        );
      });

      if (!tasks.length) {
        toast.error('Add at least one finance basic or connected-account role first.');
        setManualFinanceSaving(false);
        return;
      }

      const results = await Promise.all(tasks);
      const error = results.find((result) => result.error)?.error;
      if (error) {
        throw error;
      }

      if (hasManualFinanceChanges) {
        setProfile((current) => ({
          ...current,
          annualIncome: annualIncome ?? current.annualIncome,
        }));
        setHomeSetup((current) => current ? {
          ...current,
          monthlyRent: Object.prototype.hasOwnProperty.call(homePatch, 'monthly_rent') ? (homePatch.monthly_rent ?? null) : current.monthlyRent,
          landlordName: Object.prototype.hasOwnProperty.call(homePatch, 'landlord_name') ? (homePatch.landlord_name ?? null) : current.landlordName,
          mortgageProvider: Object.prototype.hasOwnProperty.call(homePatch, 'mortgage_provider') ? (homePatch.mortgage_provider ?? null) : current.mortgageProvider,
          utilityGasProvider: Object.prototype.hasOwnProperty.call(homePatch, 'utility_gas_provider') ? (homePatch.utility_gas_provider ?? null) : current.utilityGasProvider,
          utilityElectricProvider: Object.prototype.hasOwnProperty.call(homePatch, 'utility_electric_provider') ? (homePatch.utility_electric_provider ?? null) : current.utilityElectricProvider,
          utilityBroadbandProvider: Object.prototype.hasOwnProperty.call(homePatch, 'utility_broadband_provider') ? (homePatch.utility_broadband_provider ?? null) : current.utilityBroadbandProvider,
          utilityBroadbandContractEnd: Object.prototype.hasOwnProperty.call(homePatch, 'utility_broadband_contract_end') ? (homePatch.utility_broadband_contract_end ?? null) : current.utilityBroadbandContractEnd,
          utilityPhoneProvider: Object.prototype.hasOwnProperty.call(homePatch, 'utility_phone_provider') ? (homePatch.utility_phone_provider ?? null) : current.utilityPhoneProvider,
        } : current);
        setFinanceSetupBills(billRows.map((row) => ({
          billKey: row.bill_key,
          amount: row.amount,
          providerName: row.provider_name,
        })));
      }
      if (changedBankAccountRoles.length) {
        setBankSetupAccounts((current) =>
          current.map((row) => ({
            ...row,
            savedAccountRole: row.accountRole,
          })),
        );
      }
      setManualFinanceSaved(true);
      window.dispatchEvent(new CustomEvent('adulting:data-changed'));
      toast.success('Money & finance saved.');
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Could not save money & finance right now.');
    } finally {
      setManualFinanceSaving(false);
    }
  };

  const handleSaveHomeRoutines = async () => {
    if (!data.user?.id) {
      return;
    }

    const currentHasLawn = homeSetup?.hasLawn ?? null;
    const currentHasHouseplants = homeSetup?.hasHouseplants ?? null;
    const changed =
      homeRoutineDraft.hasLawn !== currentHasLawn
      || homeRoutineDraft.hasHouseplants !== currentHasHouseplants;

    if (!changed) {
      toast.error('Change the lawn or houseplant setup first.');
      return;
    }

    setHomeRoutineSaving(true);

    try {
      const { error } = await (supabase.from('home_profiles') as any).upsert(
        {
          user_id: data.user.id,
          has_lawn: homeRoutineDraft.hasLawn,
          has_houseplants: homeRoutineDraft.hasHouseplants,
        },
        { onConflict: 'user_id' },
      );

      if (error) {
        throw error;
      }

      setHomeSetup((current) => current ? {
        ...current,
        hasLawn: homeRoutineDraft.hasLawn,
        hasHouseplants: homeRoutineDraft.hasHouseplants,
      } : current);
      setHomeRoutineSaved(true);
      window.dispatchEvent(new CustomEvent('adulting:data-changed'));
      toast.success('Home routines saved.');
    } catch (error) {
      console.error(error);
      toast.error('Could not save your home routine setup.');
    } finally {
      setHomeRoutineSaving(false);
    }
  };

  const handleLookupChildSchools = async () => {
    if (!childSchoolLookupPostcode) {
      setChildSchoolLookupError('Add a postcode or full home address first so Adulting can find nearby schools.');
      return;
    }

    setChildSchoolLookupBusy(true);
    setChildSchoolLookupError(null);

    try {
      const { data: result, error } = await supabase.functions.invoke('family-school-calendar-import', {
        body: { postcode: childSchoolLookupPostcode, mode: 'lookup' },
      });

      if (error) {
        throw error;
      }

      if ((result as any)?.error) {
        throw new Error(String((result as any).error));
      }

      const schools = Array.isArray((result as any)?.schools)
        ? ((result as any).schools as FamilySchoolLookupCandidate[])
        : [];
      const resolvedPostcode = typeof (result as any)?.postcode === 'string'
        ? String((result as any).postcode).trim()
        : childSchoolLookupPostcode;
      if (resolvedPostcode && resolvedPostcode !== profile.postcode) {
        setDraftProfile((current) => ({ ...current, postcode: resolvedPostcode }));
      }
      setChildSchoolCandidates(schools);
      if (!schools.length) {
        setChildSchoolLookupError('No nearby schools came back yet. You can still save the child and add the school later.');
      }
    } catch (error) {
      console.error(error);
      setChildSchoolLookupError(error instanceof Error ? error.message : 'Could not look up nearby schools.');
    } finally {
      setChildSchoolLookupBusy(false);
    }
  };

  const handleImportSchoolDates = async () => {
    if (!selectedSchoolNames.length) {
      return;
    }

    if (!childSchoolLookupPostcode) {
      toast.error('Add a postcode or full home address first so Adulting can import school dates.');
      return;
    }

    setSchoolCalendarImportBusy(true);

    try {
      const { data: result, error } = await supabase.functions.invoke('family-school-calendar-import', {
        body: {
          postcode: childSchoolLookupPostcode,
          selectedSchoolNames,
        },
      });

      if (error) {
        throw error;
      }

      if ((result as any)?.error) {
        throw new Error(String((result as any).error));
      }

      toast.success(
        selectedSchoolNames.length === 1
          ? `Imported school dates for ${selectedSchoolNames[0]}.`
          : `Imported school dates for ${selectedSchoolNames.join(', ')}.`,
      );
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Could not import school dates.');
    } finally {
      setSchoolCalendarImportBusy(false);
    }
  };

  useEffect(() => {
    if (!schoolEligibleChildren.length) {
      if (childSchoolLookupAutoKeyRef.current) {
        childSchoolLookupAutoKeyRef.current = '';
      }
      if (childSchoolCandidates.length) {
        setChildSchoolCandidates([]);
      }
      if (childSchoolLookupError) {
        setChildSchoolLookupError(null);
      }
      return;
    }

    if (!childSchoolLookupAutoKey) {
      if (childSchoolLookupAutoKeyRef.current) {
        childSchoolLookupAutoKeyRef.current = '';
      }
      if (childSchoolCandidates.length) {
        setChildSchoolCandidates([]);
      }
      if (childSchoolLookupError) {
        setChildSchoolLookupError(null);
      }
      return;
    }

    if (childSchoolLookupBusy || childSchoolLookupAutoKeyRef.current === childSchoolLookupAutoKey) {
      return;
    }

    childSchoolLookupAutoKeyRef.current = childSchoolLookupAutoKey;
    void handleLookupChildSchools();
  }, [childSchoolCandidates.length, childSchoolLookupAutoKey, childSchoolLookupBusy, childSchoolLookupError, schoolEligibleChildren.length]);

  const handleResetPassword = async () => {
    if (!data.user?.email) return;

    setResetSending(true);
    const { error } = await supabase.auth.resetPasswordForEmail(data.user.email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setResetSending(false);

    if (error) {
      toast.error('Failed to send reset email');
      return;
    }

    toast.success('Reset email sent — check your inbox');
  };

  const handleDefaultSectionChange = (value: AdultingSectionPath | '') => {
    setDefaultSection(value);

    if (value) {
      localStorage.setItem('adulting_default_section', value);
      return;
    }

    localStorage.removeItem('adulting_default_section');
  };

  const persistPinnedDestinations = (nextPinnedDestinations: AdultingNavigationDestinationPath[]) => {
    setPreferredPinnedDestinations(nextPinnedDestinations);
    localStorage.setItem(ADULTING_PINNED_SECTIONS_STORAGE_KEY, JSON.stringify(nextPinnedDestinations));
    window.dispatchEvent(new CustomEvent(ADULTING_NAVIGATION_PREFERENCES_CHANGED_EVENT));
  };

  const handleDocumentsModeChange = async (mode: 'adulting' | 'google_drive' | 'icloud') => {
    if (!data.user || documentsModeSaving) return;

    setDocumentsModeSaving(true);

    try {
      const result = await updateSetupDocumentsMode({
        userId: data.user.id,
        mode,
        childNames: data.profile.children.map((child) => child.name),
      });

      if (result.settings) {
        setIntegrationSetup(result.settings);
      }

      if (result.notice?.tone === 'info') {
        toast.info(result.notice.message);
        return;
      }

      if (result.notice) {
        toast.success(mode === 'google_drive' ? 'New documents will now use your Google Drive.' : result.notice.message);
      }
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Could not update document storage.');
    } finally {
      setDocumentsModeSaving(false);
    }
  };

  const handlePinDestination = (value: AdultingNavigationDestinationPath) => {
    persistPinnedDestinations(
      pinAdultingDestination({
        preferred: preferredPinnedDestinations,
        context: navigationPreferenceContext,
        destination: value,
      }),
    );
  };

  const handleKeepDestinationUnderMore = (value: AdultingNavigationDestinationPath) => {
    persistPinnedDestinations(
      keepAdultingDestinationUnderMore({
        preferred: preferredPinnedDestinations,
        context: navigationPreferenceContext,
        destination: value,
      }),
    );
  };

  const handleMoveDestinationEarlier = (value: AdultingNavigationDestinationPath) => {
    persistPinnedDestinations(
      moveAdultingDestinationEarlier({
        preferred: preferredPinnedDestinations,
        context: navigationPreferenceContext,
        destination: value,
      }),
    );
  };

  const handleMoveDestinationLater = (value: AdultingNavigationDestinationPath) => {
    persistPinnedDestinations(
      moveAdultingDestinationLater({
        preferred: preferredPinnedDestinations,
        context: navigationPreferenceContext,
        destination: value,
      }),
    );
  };

  const handleCalendarModeChange = async (mode: 'adulting' | 'google_calendar' | 'icloud') => {
    if (!data.user || calendarModeSaving) return;

    setCalendarModeSaving(true);

    try {
      const result = await updateSetupCalendarMode({
        userId: data.user.id,
        mode,
        currentSettings: integrationSetup,
      });

      if (result.settings) {
        setIntegrationSetup(result.settings);
      }

      if (result.notice?.tone === 'info') {
        toast.info(result.notice.message);
        return;
      }

      if (result.notice) {
        toast.success(result.notice.message);
      }
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Could not update calendar mode.');
    } finally {
      setCalendarModeSaving(false);
    }
  };

  const handleHomeReminderStyleChange = async (value: ReminderStrength) => {
    if (!data.user || homeReminderStyleSaving) return;

    setHomeReminderStyleSaving(true);

    try {
      const nextSettings = await saveIntegrationSettings(data.user.id, {
        home_routine_reminder_strength: value,
      });
      setIntegrationSetup(nextSettings);
      toast.success(`Home reminders set to ${getHomeRoutineReminderStyleLabel(value).toLowerCase()}.`);
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Could not update Home reminders.');
    } finally {
      setHomeReminderStyleSaving(false);
    }
  };

  const clearAvatarDraft = () => {
    if (avatarDraftUrl) {
      URL.revokeObjectURL(avatarDraftUrl);
    }
    setAvatarDraftUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAvatarSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (avatarDraftUrl) {
      URL.revokeObjectURL(avatarDraftUrl);
    }

    setAvatarDraftUrl(URL.createObjectURL(file));
  };

  const handleAvatarUpload = async (croppedBlob: Blob) => {
    if (!data.user) {
      return;
    }

    setUploadingPhoto(true);

    try {
      const filePath = `${data.user.id}/avatar.jpg`;
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, croppedBlob, { upsert: true, contentType: 'image/jpeg' });

      if (uploadError) {
        throw uploadError;
      }

      const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(filePath);
      const avatarUrl = `${urlData.publicUrl}?t=${Date.now()}`;
      await updateProfileAvatar(avatarUrl);
      setDraftProfile((current) => ({ ...current, avatarUrl }));
      clearAvatarDraft();
      toast.success('Profile photo updated.');
    } catch (error) {
      console.error(error);
      toast.error("Couldn't upload photo. Please try again.");
    } finally {
      setUploadingPhoto(false);
    }
  };

  return (
    <AppPage className="animate-fade-in">
      <AppPageIntro
        title="My Settings"
        subtitle="Profile, setup, account, integrations, and section preferences in one place."
        action={(
          <AppButton tone="outline" size="sm" onPress={() => navigate(-1)}>
            <ArrowLeft size={16} />
            Back
          </AppButton>
        )}
      />

      <div className="space-y-6">
        <Accordion type="single" collapsible defaultValue="basics" className="space-y-6">
          <ProfileSection value="profile-photo" title="Profile photo" subtitle="Shown in Adulting menus and your profile settings across web and mobile.">
          <div className="flex flex-col gap-5 rounded-2xl border border-border bg-card/70 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <Avatar className="h-[72px] w-[72px] border border-border/70 bg-background">
                {profile.avatarUrl ? <AvatarImage src={profile.avatarUrl} alt="Profile photo" /> : null}
                <AvatarFallback className="bg-primary/15 text-2xl font-semibold text-primary">
                  {(profile.firstName || data.user?.email || 'A').trim().charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{currentProfileLabel}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {data.user?.email || 'Add a profile photo'}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Pick an image, then reposition and crop it before saving.
                </p>
              </div>
            </div>

            <div className="flex shrink-0 flex-col gap-2 sm:min-w-[180px]">
              <AppButton type="button" tone="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploadingPhoto}>
                <ImagePlus size={16} />
                {uploadingPhoto ? 'Saving...' : profile.avatarUrl ? 'Change photo' : 'Add photo'}
              </AppButton>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAvatarSelect}
              />
            </div>
          </div>
        </ProfileSection>

          <ProfileSection value="basics" title="Basics" subtitle="Used for your recommendations and forms">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="First name">
              <AppInput
                value={profile.firstName}
                onChange={(event) => setDraftProfile((current) => ({ ...current, firstName: event.target.value }))}
                placeholder="Your first name"
                autoComplete="given-name"
                spellCheck={false}
              />
            </Field>

            <Field label="Postcode">
              <AppInput
                value={profile.postcode}
                onChange={(event) => setDraftProfile((current) => ({ ...current, postcode: event.target.value }))}
                placeholder="e.g. SW1A 1AA"
                autoComplete="postal-code"
                autoCapitalize="characters"
                spellCheck={false}
              />
            </Field>

            <Field label="Employment status">
              <Select
                value={profile.employmentType || undefined}
                onValueChange={(value) => setDraftProfile((current) => ({ ...current, employmentType: value as UserProfile['employmentType'] }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="employed">Employed</SelectItem>
                  <SelectItem value="self-employed">Self-employed</SelectItem>
                  <SelectItem value="not-working">Not currently working</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
        </ProfileSection>

          <ProfileSection value="work" title="Work" subtitle="Controls whether the Work section and tools show up">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {WORK_OPTIONS.map((option) => {
              const isSelected = profile.workType === option.value;

              return (
                <AppButton
                  key={option.value}
                  tone={isSelected ? 'primary' : 'outline'}
                  size="lg"
                  onPress={() => setDraftProfile((current) => ({ ...current, workType: option.value }))}
                  justifyContent="flex-start"
                  height="auto"
                  py="$md"
                  width="100%"
                >
                  <div className="flex items-center gap-3 text-left">
                    <span className="text-2xl">{option.emoji}</span>
                    <div className="min-w-0">
                      <AppText fontSize="$sm" fontWeight="700" color={isSelected ? '$primaryForeground' : '$color'}>
                        {option.label}
                      </AppText>
                      <AppText fontSize="$xs" color={isSelected ? '$primaryForeground' : '$colorMuted'} className="mt-1">
                        {option.note}
                      </AppText>
                    </div>
                  </div>
                </AppButton>
              );
            })}
          </div>
        </ProfileSection>

          <ProfileSection value="family-care" title="Family & care" subtitle="You can change this later; it should not be locked to onboarding">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Partner name">
              <AppInput
                value={profile.partnerName}
                onChange={(event) => setDraftProfile((current) => ({ ...current, partnerName: event.target.value }))}
                placeholder="Optional"
                spellCheck={false}
              />
            </Field>
            <UkDateField
              label="Partner date of birth"
              value={profile.partnerDateOfBirth || ''}
              onChange={(nextValue) => setDraftProfile((current) => ({ ...current, partnerDateOfBirth: nextValue }))}
            />
          </div>

          <AppText fontSize="$sm" color="$colorMuted">
            Add the household basics here in My Settings, then People can simply use what you have already set up.
          </AppText>

          <div className="flex flex-wrap gap-2">
            {DEPENDANT_OPTIONS.map((option) => {
              const isSelected = option.value === 'none'
                ? profile.dependantTypes.length === 0 && activeChildren.length === 0
                : profile.dependantTypes.includes(option.value as DependantType);

              return (
                <AppButton
                  key={option.value}
                  tone={isSelected ? 'primary' : 'outline'}
                  size="sm"
                  onPress={() => toggleDependant(option.value)}
                >
                  <span className="mr-1.5">{option.emoji}</span>
                  {option.label}
                </AppButton>
              );
            })}
          </div>

          <div className="pt-3 border-t border-border/80">
            <AppSectionHeader
              title="Children"
              subtitle="Add the children whose dates, chores, and reminders should shape your Family tools."
              action={(
                <AppButton tone="outline" size="sm" onPress={addChild}>
                  <Plus size={14} />
                  Add child
                </AppButton>
              )}
            />
            {schoolEligibleChildren.length ? (
              <div className="mt-3 rounded-2xl border border-border bg-card p-4 space-y-3">
                <AppText fontSize="$sm" fontWeight="700">School confirmation</AppText>
                <AppText fontSize="$xs" color="$colorMuted">
                  For children under 18, Adulting will look up nearby schools from your postcode or saved home address, ask you to confirm the right one for each child, then let you import the shared school dates into Family.
                </AppText>
                <AppButton tone="outline" size="sm" onPress={() => void handleLookupChildSchools()} disabled={childSchoolLookupBusy}>
                  {childSchoolLookupBusy ? 'Looking up nearby schools…' : childSchoolCandidates.length ? 'Refresh nearby schools' : 'Find nearby schools'}
                </AppButton>
                {childSchoolLookupError ? (
                  <AppText fontSize="$xs" color="$colorMuted">{childSchoolLookupError}</AppText>
                ) : null}
              </div>
            ) : null}
            {profile.children.length === 0 ? (
              <AppEmptyState
                title="No children added yet"
                body="Add a child when you want school dates, deadlines, and chores to personalise around them."
              />
            ) : (
              <div className="space-y-3 mt-3">
                {profile.children.map((child) => (
                  <AppCard key={child.id} backgroundColor="$background">
                    <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_240px_auto] sm:items-end">
                      <Field label="Name">
                        <AppInput
                          placeholder="Name"
                          value={child.name}
                          onChange={(event) => updateChild(child.id, 'name', event.target.value)}
                          spellCheck={false}
                        />
                      </Field>
                      <UkDateField
                        label="Date of birth"
                        value={child.dateOfBirth || ''}
                        onChange={(nextValue) => updateChild(child.id, 'dateOfBirth', nextValue)}
                        savedAge={child.age}
                      />
                      <AppButton
                        tone="outline"
                        size="sm"
                        onPress={() => removeChild(child.id)}
                        alignSelf="flex-start"
                      >
                        <Trash2 size={14} />
                        Remove
                      </AppButton>
                    </div>
                    {isChildSchoolSelectionRelevant({ dateOfBirth: child.dateOfBirth || null, age: child.age }) && childSchoolCandidates.length ? (
                      <div className="mt-3">
                        <Field label="School">
                          <AppNativeSelect
                            value={child.schoolUrn || ''}
                            onChange={(event) => assignChildSchool(child.id, event.target.value)}
                          >
                            <option value="">Select their school</option>
                            {childSchoolCandidates.map((school) => (
                              <option key={school.urn} value={school.urn}>
                                {school.town ? `${school.name} · ${school.town}` : school.name}
                              </option>
                            ))}
                          </AppNativeSelect>
                        </Field>
                      </div>
                    ) : null}
                  </AppCard>
                ))}
              </div>
            )}
            {selectedSchoolNames.length ? (
              <div className="mt-3">
                <AppButton tone="outline" size="sm" onPress={() => void handleImportSchoolDates()} disabled={schoolCalendarImportBusy}>
                  {schoolCalendarImportBusy ? 'Importing school dates…' : 'Import school dates for selected schools'}
                </AppButton>
              </div>
            ) : null}
          </div>
        </ProfileSection>

        <ProfileSection
          value="family-setup"
          title="Family setup"
          subtitle="Partner connection, pet records, and detailed family profiles all live here in My Settings."
        >
          <div className="space-y-4">
            <HouseholdInviteSection surface="profile" />
            <FamilyProfilesSection surface="profile" />
          </div>
        </ProfileSection>

          <ProfileSection value="health-profile" title="Health profile" subtitle="Used only for reminder suggestions, not for public display">
          <div className="grid gap-4 md:grid-cols-2">
            <UkDateField
              label="Date of birth"
              value={profile.dateOfBirth}
              onChange={(nextValue) => setDraftProfile((current) => ({ ...current, dateOfBirth: nextValue }))}
            />

            <Field label="NHS invitation path on your record">
              <Select
                value={profile.screeningInvitationPath || undefined}
                onValueChange={(value) => setDraftProfile((current) => ({ ...current, screeningInvitationPath: value as UserProfile['screeningInvitationPath'] }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent>
                  {SCREENING_PATH_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="space-y-3">
            <div>
              <AppText fontSize="$sm" fontWeight="700" color="$colorMuted" className="mb-2">
                Which reminders are relevant to you?
              </AppText>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                <BooleanPill
                  label="Cervical screening"
                  value={profile.hasCervix}
                  onTrue={() => setNullableFlag('hasCervix', true)}
                  onFalse={() => setNullableFlag('hasCervix', false)}
                />
                <BooleanPill
                  label="Breast screening"
                  value={profile.breastScreeningRelevant}
                  onTrue={() => setNullableFlag('breastScreeningRelevant', true)}
                  onFalse={() => setNullableFlag('breastScreeningRelevant', false)}
                />
                <BooleanPill
                  label="Prostate / PSA discussion"
                  value={profile.prostateScreeningRelevant}
                  onTrue={() => setNullableFlag('prostateScreeningRelevant', true)}
                  onFalse={() => setNullableFlag('prostateScreeningRelevant', false)}
                />
              </div>
            </div>

            <div>
              <AppText fontSize="$sm" fontWeight="700" color="$colorMuted" className="mb-2">
                HRT
              </AppText>
              <AppButton
                tone={profile.onHrt ? 'primary' : 'outline'}
                size="sm"
                onPress={() => setDraftProfile((current) => ({ ...current, onHrt: !current.onHrt }))}
              >
                {profile.onHrt ? 'On HRT or want HRT review reminders' : 'Not on HRT'}
              </AppButton>
            </div>

            <AppText fontSize="$sm" color="$colorMuted">
              This helps Adulting suggest reminders for things like cervical screening, breast screening, PSA /
              prostate discussion, NHS Health Check, and HRT reviews. You can still add any reminder manually.
            </AppText>
          </div>
        </ProfileSection>

            <ProfileSetupPanel
              value="home-vehicle"
              title="Home & vehicle"
              subtitle="Address, living situation, council tax, utilities, rent or mortgage, and vehicle details."
              status={homeSetupStatus}
            >
              {setupLoading ? (
                <AppText fontSize="$sm" color="$colorMuted">
                  Loading your saved home setup…
                </AppText>
              ) : (
                <div className="space-y-1">
                  <AppText fontSize="$sm" fontWeight="700">
                    {homeSetup?.address?.trim() || 'No home address saved yet'}
                  </AppText>
                  <AppText fontSize="$xs" color="$colorMuted">
                    Tenure: {formatTenureLabel(homeSetup?.tenure)}
                  </AppText>
                  <AppText fontSize="$xs" color="$colorMuted">
                    Council tax: {homeSetup?.councilTaxBand ? `Band ${homeSetup.councilTaxBand}` : 'Not set'}
                  </AppText>
                  <AppText fontSize="$xs" color="$colorMuted">
                    Vehicle: {homeSetup?.vehicleRegistrationNumber || 'No vehicle saved'}
                  </AppText>
                  <AppText fontSize="$xs" color="$colorMuted">
                    MOT due: {formatSetupDate(homeSetup?.vehicleMotDueDate)}
                    {' · '}
                    Tax due: {formatSetupDate(homeSetup?.vehicleTaxDueDate)}
                  </AppText>
                </div>
              )}
              <AppText fontSize="$sm" color="$colorMuted">
                These details are part of your setup here in My Settings. Home itself is for using bills, routines, and property admin after the setup is already in place.
              </AppText>
              <div className="mt-4 space-y-4 rounded-2xl border border-border bg-background/80 p-4">
                <div className="space-y-2">
                  <AppText fontSize="$sm" fontWeight="700">Home routines</AppText>
                  <AppText fontSize="$xs" color="$colorMuted">
                    Tell Adulting if this home really has a lawn or houseplants so Home reminders stay relevant instead of noisy.
                  </AppText>
                  {homeRoutineSaved ? (
                    <AppInlineAlert title="Home routines saved">
                      Adulting will keep lawn and houseplant reminders matched to this home.
                    </AppInlineAlert>
                  ) : null}
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <BooleanPill
                    label="Lawn"
                    value={homeRoutineDraft.hasLawn}
                    onTrue={() => {
                      setHomeRoutineSaved(false);
                      setHomeRoutineDraft((current) => ({ ...current, hasLawn: true }));
                    }}
                    onFalse={() => {
                      setHomeRoutineSaved(false);
                      setHomeRoutineDraft((current) => ({ ...current, hasLawn: false }));
                    }}
                    onClear={() => {
                      setHomeRoutineSaved(false);
                      setHomeRoutineDraft((current) => ({ ...current, hasLawn: null }));
                    }}
                  />
                  <BooleanPill
                    label="Houseplants"
                    value={homeRoutineDraft.hasHouseplants}
                    onTrue={() => {
                      setHomeRoutineSaved(false);
                      setHomeRoutineDraft((current) => ({ ...current, hasHouseplants: true }));
                    }}
                    onFalse={() => {
                      setHomeRoutineSaved(false);
                      setHomeRoutineDraft((current) => ({ ...current, hasHouseplants: false }));
                    }}
                    onClear={() => {
                      setHomeRoutineSaved(false);
                      setHomeRoutineDraft((current) => ({ ...current, hasHouseplants: null }));
                    }}
                  />
                </div>
                <AppButton
                  tone="outline"
                  onPress={() => void handleSaveHomeRoutines()}
                  disabled={homeRoutineSaving}
                >
                  {homeRoutineSaving ? 'Saving home routines…' : 'Save home routines'}
                </AppButton>
              </div>
            </ProfileSetupPanel>

            <ProfileSetupPanel
              value="money-banking"
              title={SETUP_FINANCE_SECTION_TITLE}
              subtitle={SETUP_FINANCE_SECTION_SUBTITLE}
              status={moneySetupStatus}
            >
              {setupLoading ? (
                <AppText fontSize="$sm" color="$colorMuted">
                  Loading your banking setup…
                </AppText>
              ) : (
                <div className="space-y-1">
                  <AppText fontSize="$sm" fontWeight="700">
                    {bankSetup?.connectedAccountCount
                      ? `${bankSetup.connectedAccountCount} connected bank account${bankSetup.connectedAccountCount === 1 ? '' : 's'}`
                      : 'No connected bank accounts yet'}
                  </AppText>
                  <AppText fontSize="$xs" color="$colorMuted">
                    Latest sync: {bankSetup?.latestSyncedAt ? new Date(bankSetup.latestSyncedAt).toLocaleString('en-GB') : 'Not synced yet'}
                  </AppText>
                </div>
              )}
              <AppText fontSize="$sm" color="$colorMuted">
                {SETUP_FINANCE_HANDOFF_NOTE}
              </AppText>
              {data.user ? (
                <div className="mt-3">
                  <ConnectBankButton
                    userId={data.user.id}
                    redirectUrl={typeof window !== 'undefined' ? `${window.location.origin}/profile` : undefined}
                  />
                </div>
              ) : null}
              {bankSetupAccounts.length ? (
                <div className="mt-4 space-y-4 rounded-2xl border border-border bg-background/80 p-4">
                  <div className="space-y-2">
                    <AppText fontSize="$sm" fontWeight="700">Connected account roles</AppText>
                    <AppText fontSize="$xs" color="$colorMuted">
                      Tell Adulting which connected accounts are personal, shared household, or self-employed business. Business accounts stay out of the personal Money view until Work is ready to handle them separately.
                    </AppText>
                  </div>
                  {bankSetupAccounts.map((account) => {
                    const selectedRole =
                      FINANCE_BANK_ACCOUNT_ROLE_OPTIONS.find((option) => option.value === account.accountRole)
                      || FINANCE_BANK_ACCOUNT_ROLE_OPTIONS[0];

                    return (
                      <div key={account.id} className="space-y-3 rounded-xl border border-border bg-card p-4">
                        <div className="space-y-1">
                          <AppText fontSize="$sm" fontWeight="700">
                            {account.name || account.institutionName || 'Connected account'}
                          </AppText>
                          <AppText fontSize="$xs" color="$colorMuted">
                            {[account.institutionName, formatBankAccountTypeLabel(account.accountType), account.lastSynced ? `synced ${new Date(account.lastSynced).toLocaleDateString('en-GB')}` : null]
                              .filter(Boolean)
                              .join(' · ')}
                          </AppText>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {FINANCE_BANK_ACCOUNT_ROLE_OPTIONS.map((option) => (
                            <AppButton
                              key={`${account.id}-${option.value}`}
                              size="sm"
                              tone={account.accountRole === option.value ? 'primary' : 'outline'}
                              onPress={() => setBankSetupAccounts((current) =>
                                current.map((row) => row.id === account.id ? { ...row, accountRole: option.value } : row),
                              )}
                            >
                              {option.shortLabel}
                            </AppButton>
                          ))}
                        </div>
                        <AppText fontSize="$xs" color="$colorMuted">
                          {selectedRole.description}
                        </AppText>
                      </div>
                    );
                  })}
                </div>
              ) : null}
              <div className="mt-4 space-y-4 rounded-2xl border border-border bg-background/80 p-4">
                <div className="space-y-2">
                  <AppText fontSize="$sm" fontWeight="700">Finance basics</AppText>
                  <AppText fontSize="$xs" color="$colorMuted">
                    Add the core money picture manually here if you want control, connect banking below if you want speed, or use both together. Adulting then reuses the same secure baseline across Money, Home, Work, and future analysis.
                  </AppText>
                  {manualFinanceSaved ? (
                    <AppInlineAlert title="Money & finance saved">
                      Adulting can now reuse the same finance baseline across your Adulting devices and sections.
                    </AppInlineAlert>
                  ) : null}
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Monthly household income (£)">
                    <AppInput
                      type="number"
                      inputMode="decimal"
                      value={manualFinanceDraft.monthlyIncome}
                      onChange={(event) => setManualFinanceDraft((current) => ({ ...current, monthlyIncome: event.target.value }))}
                      placeholder="e.g. 2400"
                    />
                  </Field>

                  <Field label={getManualFinanceHousingCostLabel(homeSetup?.tenure)}>
                    <AppInput
                      type="number"
                      inputMode="decimal"
                      value={manualFinanceDraft.housingMonthlyCost}
                      onChange={(event) => setManualFinanceDraft((current) => ({ ...current, housingMonthlyCost: event.target.value }))}
                      placeholder="Optional"
                    />
                  </Field>

                  <Field label={getManualFinanceHousingProviderLabel(homeSetup?.tenure)}>
                    <AppInput
                      value={manualFinanceDraft.housingProvider}
                      onChange={(event) => setManualFinanceDraft((current) => ({ ...current, housingProvider: event.target.value }))}
                      placeholder="Optional"
                    />
                  </Field>

                  <Field label="Council tax monthly (£)">
                    <AppInput
                      type="number"
                      inputMode="decimal"
                      value={manualFinanceDraft.councilTaxMonthly}
                      onChange={(event) => setManualFinanceDraft((current) => ({ ...current, councilTaxMonthly: event.target.value }))}
                      placeholder="Optional"
                    />
                  </Field>

                  <Field label="Energy provider">
                    <AppInput
                      value={manualFinanceDraft.energyProvider}
                      onChange={(event) => setManualFinanceDraft((current) => ({ ...current, energyProvider: event.target.value }))}
                      placeholder="Optional"
                    />
                  </Field>

                  <Field label="Gas monthly (£)">
                    <AppInput
                      type="number"
                      inputMode="decimal"
                      value={manualFinanceDraft.gasMonthly}
                      onChange={(event) => setManualFinanceDraft((current) => ({ ...current, gasMonthly: event.target.value }))}
                      placeholder="Optional"
                    />
                  </Field>

                  <Field label="Electricity monthly (£)">
                    <AppInput
                      type="number"
                      inputMode="decimal"
                      value={manualFinanceDraft.electricityMonthly}
                      onChange={(event) => setManualFinanceDraft((current) => ({ ...current, electricityMonthly: event.target.value }))}
                      placeholder="Optional"
                    />
                  </Field>

                  <Field label="Water monthly (£)">
                    <AppInput
                      type="number"
                      inputMode="decimal"
                      value={manualFinanceDraft.waterMonthly}
                      onChange={(event) => setManualFinanceDraft((current) => ({ ...current, waterMonthly: event.target.value }))}
                      placeholder="Optional"
                    />
                  </Field>

                  <Field label="Broadband provider">
                    <AppInput
                      value={manualFinanceDraft.broadbandProvider}
                      onChange={(event) => setManualFinanceDraft((current) => ({ ...current, broadbandProvider: event.target.value }))}
                      placeholder="Optional"
                    />
                  </Field>

                  <Field label="Broadband monthly (£)">
                    <AppInput
                      type="number"
                      inputMode="decimal"
                      value={manualFinanceDraft.broadbandMonthly}
                      onChange={(event) => setManualFinanceDraft((current) => ({ ...current, broadbandMonthly: event.target.value }))}
                      placeholder="Optional"
                    />
                  </Field>

                  <UkDateField
                    label="Broadband contract end"
                    value={manualFinanceDraft.broadbandContractEnd}
                    onChange={(nextValue) => setManualFinanceDraft((current) => ({ ...current, broadbandContractEnd: nextValue }))}
                    blankHint="Optional"
                  />

                  <Field label="Mobile provider">
                    <AppInput
                      value={manualFinanceDraft.phoneProvider}
                      onChange={(event) => setManualFinanceDraft((current) => ({ ...current, phoneProvider: event.target.value }))}
                      placeholder="Optional"
                    />
                  </Field>

                  <div className="sm:col-span-2">
                    <Field label="Mobile phone monthly (£)">
                      <AppInput
                        type="number"
                        inputMode="decimal"
                        value={manualFinanceDraft.mobilePhoneMonthly}
                        onChange={(event) => setManualFinanceDraft((current) => ({ ...current, mobilePhoneMonthly: event.target.value }))}
                        placeholder="Optional"
                      />
                    </Field>
                  </div>
                </div>

                <AppButton onPress={() => void handleSaveManualFinanceBasics()} disabled={manualFinanceSaving}>
                  {manualFinanceSaving ? 'Saving…' : 'Save money & finance'}
                </AppButton>
              </div>
            </ProfileSetupPanel>

            <ProfileSetupPanel
              value="storage-calendar"
              title="Storage & calendar"
              subtitle="Choose whether Adulting keeps files and dates inside Adulting, mirrors them to connected services, or uses a mix of both."
              status={storageCalendarSetupStatus}
            >
              {setupLoading ? (
                <AppText fontSize="$sm" color="$colorMuted">
                  Loading your storage and calendar setup…
                </AppText>
              ) : (
                <div className="space-y-1">
                  <AppText fontSize="$sm" fontWeight="700">
                    Documents: {formatDocumentsMode(integrationSetup)}
                  </AppText>
                  <AppText fontSize="$xs" color="$colorMuted">
                    Calendar: {formatCalendarMode(integrationSetup)}
                  </AppText>
                  <AppText fontSize="$xs" color="$colorMuted">
                    Calendar sync: {integrationSetup?.calendar_last_synced_at ? new Date(integrationSetup.calendar_last_synced_at).toLocaleString('en-GB') : 'Not synced yet'}
                  </AppText>
                </div>
              )}
              <AppText fontSize="$sm" color="$colorMuted">
                Adulting-managed storage and calendar stay available across your Adulting devices. Connect Google or Apple services when you want faster mirroring into your own tools, or mix both without changing the day-to-day sections.
              </AppText>
              <AppText fontSize="$xs" color="$colorMuted" className="mt-3">
                Documents
              </AppText>
              <AppButtonRow className="mt-2">
                <AppButton
                  size="sm"
                  tone={integrationSetup?.documents_mode !== 'google_drive' ? 'primary' : 'outline'}
                  onPress={() => void handleDocumentsModeChange('adulting')}
                  disabled={setupLoading || documentsModeSaving}
                >
                  <ProviderButtonLabel icon={<AdultingMark />} label="Adulting storage" />
                </AppButton>
                <AppButton
                  size="sm"
                  tone={integrationSetup?.documents_mode === 'google_drive' ? 'primary' : 'outline'}
                  onPress={() => void handleDocumentsModeChange('google_drive')}
                  disabled={setupLoading || documentsModeSaving}
                >
                  <ProviderButtonLabel
                    icon={<GoogleMark />}
                    label={documentsModeSaving && integrationSetup?.documents_mode !== 'google_drive' ? 'Connecting…' : 'Google Drive'}
                  />
                </AppButton>
                <AppButton
                  size="sm"
                  tone="outline"
                  onPress={() => void handleDocumentsModeChange('icloud')}
                  disabled={setupLoading || documentsModeSaving}
                >
                  <ProviderButtonLabel icon={<AppleMark />} label="Apple Files" />
                </AppButton>
              </AppButtonRow>
              <AppText fontSize="$xs" color="$colorMuted" className="mt-2">
                Choose the model that suits you: keep documents in Adulting, mirror new files into Google Drive, or use Apple Files from the iPhone app alongside Adulting-managed storage.
              </AppText>
              {integrationSetup?.documents_mode === 'google_drive' ? (
                <div className="mt-3">
                  <AppInlineAlert title="Google Drive connected">
                    {integrationSetup.google_account_email ? `${integrationSetup.google_account_email}. ` : ''}
                    Root folder: {integrationSetup.documents_root_folder_name || 'Adulting'}.
                  </AppInlineAlert>
                </div>
              ) : null}
              <AppButtonRow className="mt-3">
                <AppButton size="sm" tone="outline" onPress={() => navigate('/documents')}>
                  Open Documents
                </AppButton>
                {integrationSetup?.documents_mode === 'google_drive' && integrationSetup.documents_root_folder_url ? (
                  <AppButton
                    size="sm"
                    tone="outline"
                    onPress={() => window.open(integrationSetup.documents_root_folder_url || '', '_blank', 'noopener,noreferrer')}
                  >
                    Open Google Drive
                  </AppButton>
                ) : null}
              </AppButtonRow>

              <AppText fontSize="$xs" color="$colorMuted" className="mt-4">
                Calendar
              </AppText>
              <AppButtonRow className="mt-2">
                <AppButton
                  size="sm"
                  tone={integrationSetup?.calendar_mode === 'adulting' ? 'primary' : 'outline'}
                  onPress={() => void handleCalendarModeChange('adulting')}
                  disabled={setupLoading || calendarModeSaving}
                >
                  <ProviderButtonLabel icon={<AdultingMark />} label="Adulting calendar" />
                </AppButton>
                <AppButton
                  size="sm"
                  tone={integrationSetup?.calendar_mode === 'google_calendar' ? 'primary' : 'outline'}
                  onPress={() => void handleCalendarModeChange('google_calendar')}
                  disabled={setupLoading || calendarModeSaving}
                >
                  <ProviderButtonLabel
                    icon={<GoogleMark />}
                    label={calendarModeSaving && integrationSetup?.calendar_mode !== 'google_calendar' ? 'Connecting…' : 'Google Calendar'}
                  />
                </AppButton>
                <AppButton
                  size="sm"
                  tone={integrationSetup?.calendar_mode === 'icloud' ? 'primary' : 'outline'}
                  onPress={() => void handleCalendarModeChange('icloud')}
                  disabled={setupLoading || calendarModeSaving}
                >
                  <ProviderButtonLabel
                    icon={<AppleMark />}
                    label={calendarModeSaving && integrationSetup?.calendar_mode !== 'icloud' ? 'Saving…' : 'Apple Calendar'}
                  />
                </AppButton>
              </AppButtonRow>
              <AppText fontSize="$xs" color="$colorMuted" className="mt-2">
                Adulting calendar is the main calendar view inside Adulting. Connect Google Calendar or Apple Calendar when you want Adulting to keep a copy in your own calendar too.
              </AppText>
              {integrationSetup?.calendar_mode === 'google_calendar' ? (
                <div className="mt-3">
                  <AppInlineAlert title={integrationSetup.calendar_sync_status === 'error' ? 'Google Calendar needs another sync' : 'Google Calendar ready'}>
                    {integrationSetup.calendar_external_name || 'Adulting LifeOS'}
                    {integrationSetup.google_account_email ? ` · ${integrationSetup.google_account_email}` : ''}.
                    {' '}
                    {formatCalendarSyncStatus(
                      integrationSetup.calendar_last_synced_at,
                      integrationSetup.calendar_sync_status,
                    )}
                  </AppInlineAlert>
                </div>
              ) : integrationSetup?.calendar_mode === 'icloud' ? (
                <div className="mt-3">
                  <AppInlineAlert title="Apple Calendar ready">
                    {integrationSetup.calendar_external_name || 'Adulting LifeOS'}. Use the Adulting iPhone app to sync and keep that Apple Calendar copy updated.
                  </AppInlineAlert>
                </div>
              ) : null}
              <AppButtonRow className="mt-3">
                <AppButton size="sm" tone="outline" onPress={() => navigate('/calendar')}>
                  Open Calendar
                </AppButton>
                {integrationSetup?.calendar_mode === 'google_calendar' && integrationSetup.calendar_external_url ? (
                  <AppButton
                    size="sm"
                    tone="outline"
                    onPress={() => window.open(integrationSetup.calendar_external_url || '', '_blank', 'noopener,noreferrer')}
                  >
                    Open Google Calendar
                  </AppButton>
                ) : null}
              </AppButtonRow>
            </ProfileSetupPanel>

            <ProfileSetupPanel
              value="account-data"
              title="Account, privacy & data"
              subtitle="Password, account controls, export, deletion, and the legal pages explaining how Adulting handles your data."
              status={accountSetupStatus}
            >
              {setupLoading ? (
                <AppText fontSize="$sm" color="$colorMuted">
                  Loading your account setup…
                </AppText>
              ) : (
                <div className="space-y-1">
                  <AppText fontSize="$sm" fontWeight="700">
                    Start section: {defaultSection ? ADULTING_SECTION_OPTION_LABELS[defaultSection] : 'Remember last section'}
                  </AppText>
                  <AppText fontSize="$xs" color="$colorMuted">
                    Mobile push: {integrationSetup?.push_notifications_enabled ? 'On for at least one device' : 'Off'}
                  </AppText>
                  <AppText fontSize="$xs" color="$colorMuted">
                    Last mobile push registration: {integrationSetup?.push_last_registered_at ? new Date(integrationSetup.push_last_registered_at).toLocaleString('en-GB') : 'No device registered yet'}
                  </AppText>
                  <AppText fontSize="$xs" color="$colorMuted">
                    Home reminders: {formatHomeRoutineReminderStyle(integrationSetup, setupLoading)}
                  </AppText>
                  <AppText fontSize="$xs" color="$colorMuted">
                    Pinned main navigation: {resolvedPinnedDestinations.map((path) => getAdultingDestinationPlainLabel(path)).join(', ')}
                  </AppText>
                  <AppText fontSize="$xs" color="$colorMuted">
                    Under More: {overflowDestinations.length ? overflowDestinations.map((path) => getAdultingDestinationPlainLabel(path)).join(', ') : 'Nothing right now'}
                  </AppText>
                </div>
              )}
              <AppText fontSize="$sm" color="$colorMuted">
                Password, alerts, app defaults, export, deletion, and legal controls are changed here in My Settings. The main sections are for using the product after setup is already in place.
              </AppText>
              <AppText fontSize="$xs" color="$colorMuted" className="mt-3">
                Start in this section
              </AppText>
              <AppButtonRow className="mt-2">
                {sectionOptions.map((option) => (
                  <AppButton
                    key={option.value || 'remember-last-section'}
                    size="sm"
                    tone={defaultSection === option.value ? 'primary' : 'outline'}
                    onPress={() => handleDefaultSectionChange(option.value)}
                  >
                    {option.label}
                  </AppButton>
                ))}
              </AppButtonRow>
              <AppText fontSize="$xs" color="$colorMuted" className="mt-4">
                Main navigation priority
              </AppText>
              <AppText fontSize="$xs" color="$colorMuted" className="mt-2">
                Adulting keeps four destinations pinned. Move the ones you use most earlier in the list. Everything else stays under More in the same order.
              </AppText>
              <div className="mt-3 space-y-3">
                {navigationDestinationOptions.map((path, index) => {
                  const pinnedIndex = resolvedPinnedDestinations.indexOf(path);
                  const isPinned = pinnedIndex >= 0;

                  return (
                    <AppCard key={path}>
                      <AppCardBody gap="$sm">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <AppText fontSize="$sm" fontWeight="700">
                              {ADULTING_DESTINATION_OPTION_LABELS[path]}
                            </AppText>
                            <AppText fontSize="$xs" color="$colorMuted">
                              {isPinned
                                ? `Pinned in slot ${pinnedIndex + 1}`
                                : 'Kept under More'}
                            </AppText>
                          </div>
                          <AppText fontSize="$xs" color="$colorMuted">
                            Priority {index + 1}
                          </AppText>
                        </div>
                        <AppButtonRow>
                          <AppButton
                            size="sm"
                            tone={isPinned ? 'outline' : 'primary'}
                            onPress={() => (isPinned ? handleKeepDestinationUnderMore(path) : handlePinDestination(path))}
                          >
                            {isPinned ? 'Keep under More' : 'Pin to main navigation'}
                          </AppButton>
                          <AppButton
                            size="sm"
                            tone="outline"
                            onPress={() => handleMoveDestinationEarlier(path)}
                            disabled={index === 0}
                          >
                            Move earlier
                          </AppButton>
                          <AppButton
                            size="sm"
                            tone="outline"
                            onPress={() => handleMoveDestinationLater(path)}
                            disabled={index === navigationDestinationOptions.length - 1}
                          >
                            Move later
                          </AppButton>
                        </AppButtonRow>
                      </AppCardBody>
                    </AppCard>
                  );
                })}
              </div>
              <div className="mt-3">
                <AppInlineAlert title={integrationSetup?.push_notifications_enabled ? 'Mobile push is on' : 'Mobile push is off'}>
                  {formatPushStatus(integrationSetup, setupLoading)}
                </AppInlineAlert>
              </div>
              <AppText fontSize="$xs" color="$colorMuted" className="mt-3">
                Home routine reminder style
              </AppText>
              <AppButtonRow className="mt-2">
                {HOME_ROUTINE_REMINDER_STYLE_OPTIONS.map((option) => (
                  <AppButton
                    key={option.value}
                    size="sm"
                    tone={integrationSetup?.home_routine_reminder_strength === option.value ? 'primary' : 'outline'}
                    onPress={() => void handleHomeReminderStyleChange(option.value)}
                    disabled={homeReminderStyleSaving}
                  >
                    {homeReminderStyleSaving && integrationSetup?.home_routine_reminder_strength === option.value
                      ? 'Saving…'
                      : option.label}
                  </AppButton>
                ))}
              </AppButtonRow>
              <AppText fontSize="$xs" color="$colorMuted" className="mt-2">
                Applies to lawn, houseplants, and the recurring Home jobs. Change it here once instead of managing every card separately.
              </AppText>
              <AppButtonRow className="mt-3">
                <AppButton size="sm" tone="outline" onPress={() => navigate('/privacy')}>
                  Privacy
                </AppButton>
                <AppButton size="sm" tone="outline" onPress={() => navigate('/gdpr')}>
                  GDPR
                </AppButton>
              </AppButtonRow>
              <AppButtonRow>
                <AppButton size="sm" tone="outline" onPress={handleResetPassword} disabled={resetSending || !data.user?.email}>
                  {resetSending ? 'Sending…' : 'Change password'}
                </AppButton>
                <AppButton size="sm" tone="outline" onPress={() => void signOut()}>
                  Sign out
                </AppButton>
              </AppButtonRow>
              <AppText fontSize="$xs" color="$colorMuted">
                Use the main sections for day-to-day tasks. Use My Settings when you want to change how Adulting is configured.
              </AppText>
            </ProfileSetupPanel>
          </Accordion>

        <AppButtonRow justifyContent="flex-end">
          <AppButton onPress={handleSave} disabled={saving} width="100%" className="md:w-auto md:min-w-[220px]">
            <Save size={16} />
            {saving ? 'Saving…' : 'Save profile'}
          </AppButton>
        </AppButtonRow>
      </div>

      <AvatarCropDialog
        open={Boolean(avatarDraftUrl)}
        imageSrc={avatarDraftUrl}
        saving={uploadingPhoto}
        onCancel={clearAvatarDraft}
        onConfirm={handleAvatarUpload}
      />
    </AppPage>
  );
}

function ProfileSection({
  value,
  title,
  subtitle: _subtitle,
  children,
}: {
  value: string;
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <AccordionItem value={value} className="overflow-hidden rounded-2xl border-0 bg-card shadow-sm shadow-primary/5">
      <AccordionTrigger className="px-4 py-4 text-left hover:no-underline">
        <div className="flex w-full items-start gap-3 pr-4 text-left">
          <div className="min-w-0 flex-1">
            <AppText fontSize="$sm" fontWeight="700">{title}</AppText>
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="pt-0">
        <AppCardBody>{children}</AppCardBody>
      </AccordionContent>
    </AccordionItem>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <AppFieldGroup label={label}>{children}</AppFieldGroup>;
}

function ProfileSetupPanel({
  value,
  title,
  subtitle: _subtitle,
  status,
  children,
}: {
  value: string;
  title: string;
  subtitle: string;
  status: SetupStatusSummary;
  children: React.ReactNode;
}) {
  return (
    <AccordionItem value={value} className="rounded-2xl border-0 bg-background/70 px-3">
      <AccordionTrigger className="py-3 text-left hover:no-underline">
        <div className="flex w-full items-start gap-3 pr-4 text-left">
          <div className="min-w-0 flex-1">
            <AppText fontSize="$sm" fontWeight="700">{title}</AppText>
          </div>
          <AppBadge className={getSetupStatusBadgeClassName(status.tone)}>
            {status.label}
          </AppBadge>
        </div>
      </AccordionTrigger>
      <AccordionContent className="pb-0 pt-1">
        <div className="space-y-3 pb-2">
          {children}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

function BooleanPill({
  label,
  value,
  onTrue,
  onFalse,
  onClear,
}: {
  label: string;
  value: boolean | null;
  onTrue: () => void;
  onFalse: () => void;
  onClear?: () => void;
}) {
  return (
    <AppCard backgroundColor="$background" minWidth={0}>
      <AppText fontSize="$sm" fontWeight="700">{label}</AppText>
      <AppButtonRow>
        <AppButton
          tone={value === true ? 'primary' : 'outline'}
          size="sm"
          onPress={onTrue}
          flex={1}
        >
          Yes
        </AppButton>
        <AppButton
          tone={value === false ? 'primary' : 'outline'}
          size="sm"
          onPress={onFalse}
          flex={1}
        >
          No
        </AppButton>
        {onClear ? (
          <AppButton
            tone={value === null ? 'primary' : 'outline'}
            size="sm"
            onPress={onClear}
            flex={1}
          >
            Clear
          </AppButton>
        ) : null}
      </AppButtonRow>
    </AppCard>
  );
}
